import React, { FormEvent, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { PageComponent } from "../components/Page";
import Row from "../components/Row";
import VerticalSplitPage from "../components/VerticalSplitPage";
import BlockButton from "../components/BlockButton";
import Required from "../components/Required";
import {
  ExactDeliveriesForWeekdayUI,
  RandomizedDeliveryForWeekdayComponent,
  RandomizedDeliveryForWeekdayDefaultValue,
} from "../components/ema/EMADelivery";
import { RouteComponentProps } from "@reach/router";
import {
  BooleanQuestion,
  ChoiceQuestion,
  ExactDeliveriesForWeekday,
  ExactDeliverySurvey,
  RandomizedDeliverySurvey,
  RandomizedDeliveryWindowForWeekday,
  ScaleQuestion,
  ShuffleSequence,
  StudySurvey,
  SurveyQuestion,
  TextEntryQuestion,
  TextInfo,
  TimeOfDayQuestion,
  Weekday,
} from "../models";
import { SurveyQuestionTypes, BranchingRules } from "../shared/Types";
import { navigateToCreateStudy, SurveyPrefsDefaultValue } from "./StudyCreator";
import Switch from "../components/Switch";
import QuestionEditor from "../components/ema/QuestionEditor";
import ScheduleControlPanel, {
  ScheduleControlPanelValue,
} from "../components/ema/ScheduleControlPanel";
import { SmallButton } from "../components/SmallButton";
import QuestionControlPanel, {
  QuestionControlPanelValue,
} from "../components/ema/QuestionControlPanel";
import { ValidatedInput, ValidatedTextArea } from "../components/Validation";
import {
  BranchingValidator,
  NumberValidator,
  StringValidator,
} from "../shared/utils/Validators";
import { scheduleControlPanelValidation } from "../utils/formValidation";
import { alertMounter } from "../components/Alert";
import questionTypeIsNull from "../utils/questionTypeIsNull";
import getQuestionContent from "../utils/getQuestionContent";
import getBranchingRule, {
  questionsWithBlankBranchDestinations,
} from "../utils/getBranchingRule";
import { useDashboardSettings } from "../utils/useDashboardSettings";
import { hasRiskRules } from "../utils/riskRulesUtils";
import { SCREENTIME_EMA_NAME } from "../shared/Constants";

export const RandomizedDefaultSurveyValue: StudySurvey = {
  random: {
    surveyId: "",
    notificationHeader: "",
    notificationBody: "",
    weekdaysAvailable: [],
    totalNumberOfDays: undefined,
    introStepHeader: "",
    introStepBody: "",
    completionStepEnabled: true,
    completionStepHeader: "",
    completionStepBody: "",
    emaQuestions: [],
    containsBranchingLogic: false,
    containsShuffledSequence: false,
    shuffleSequence: undefined,
    containsRiskAlerts: undefined,
    multipleSurveyBursts: false,
    daysWithinSurveyBurst: undefined,
    daysBetweenSurveyBursts: undefined,
    daysBeforeFirstSurveyDelivery: undefined,
    selfInitiatedSurvey: false,
    weekDeliverySchedule: [],
  },
  exact: undefined,
};

export const ExactDefaultSurveyValue: StudySurvey = {
  exact: {
    surveyId: "",
    notificationHeader: "",
    notificationBody: "",
    weekdaysAvailable: [],
    totalNumberOfDays: undefined,
    introStepHeader: "",
    introStepBody: "",
    completionStepEnabled: false,
    completionStepHeader: "",
    completionStepBody: "",
    emaQuestions: [],
    containsBranchingLogic: false,
    containsShuffledSequence: false,
    shuffleSequence: undefined,
    containsRiskAlerts: undefined,
    multipleSurveyBursts: false,
    daysWithinSurveyBurst: undefined,
    daysBetweenSurveyBursts: undefined,
    daysBeforeFirstSurveyDelivery: undefined,
    selfInitiatedSurvey: false,
    weekDeliverySchedule: [],
  },
  random: undefined,
};

export const updateBranchingForIDChanges = (
  oldQuestion: SurveyQuestionTypes | undefined,
  updatedQuestion: SurveyQuestionTypes,
  currentQuestions: Array<SurveyQuestion | null>
) => {
  const questionsCopy = [...currentQuestions];

  const oldQuestionID = oldQuestion?.questionId;
  const newQuestionID = updatedQuestion.questionId;

  if (oldQuestionID === newQuestionID) return questionsCopy;

  questionsCopy.forEach((question, index) => {
    const questionCopy = { ...questionsCopy[index] } as SurveyQuestion;
    const questionTypeCopy:
      | SurveyQuestionTypes
      | undefined = getQuestionContent(questionCopy);
    const branchingRuleCopy: BranchingRules | undefined = getBranchingRule(
      questionCopy
    );

    Object.keys(question as string).forEach((questionType) => {
      if (!questionTypeIsNull(question, questionType) && branchingRuleCopy) {
        questionsCopy[index] = {
          ...questionsCopy[index],
          [questionType]: {
            ...questionTypeCopy,
            branchingRule: {
              ...branchingRuleCopy,
              defaultQuestionId:
                branchingRuleCopy?.defaultQuestionId === oldQuestionID
                  ? newQuestionID
                  : branchingRuleCopy?.defaultQuestionId,
              destinationQuestionId:
                branchingRuleCopy?.destinationQuestionId === oldQuestionID
                  ? newQuestionID
                  : branchingRuleCopy?.destinationQuestionId, 
              ...("destinations" in branchingRuleCopy && branchingRuleCopy.destinations !== undefined && branchingRuleCopy.destinations !== null
                ? {
                    destinations: branchingRuleCopy.destinations.map(destination =>
                      destination && {
                        ...destination,
                        destinationQuestionId: destination.destinationQuestionId === oldQuestionID
                          ? newQuestionID
                          : destination.destinationQuestionId,
                      }
                    ),
                  }
                : {}),
            },
          },
        };
      }
    });
  });

  return questionsCopy;
};

/**
 * Parses a numeric HTML input value. Returns `undefined` for empty strings
 * so that optional numeric fields are omitted from the GraphQL payload
 * (AppSync rejects empty strings for Int fields).
 */
export function parseNumericInput(value: string): number | undefined {
  return value === "" ? undefined : parseInt(value, 10);
}

/**
 * Returns true when a schedule entry should be created for `currentDay`:
 * - the day is present in `weekdaysAvailable` (i.e. it is selected), AND
 * - there is no existing entry for it in `weekDeliverySchedule`.
 *
 * Returning false when the day is NOT selected prevents the useEffect from
 * re-adding an entry immediately after the user removes a day via the "X" button.
 */
export function shouldEnsureScheduleEntry(
  currentDay: string,
  weekdaysAvailable: string[],
  weekDeliverySchedule: Array<{ dayOfWeek: string } | null>
): boolean {
  const dayIsSelected = weekdaysAvailable.includes(currentDay);
  const scheduleExists = weekDeliverySchedule.some(
    (item) => item && item.dayOfWeek === currentDay
  );
  return dayIsSelected && !scheduleExists;
}

/**
 * Normalizes an optional numeric text input to a nullable number.
 * - empty string => null (so the attribute is explicitly present as null in DynamoDB)
 * - non-empty => Number(raw)
 */
function parseNullableNumberInput(raw: string): number | null {
  return raw === "" ? null : Number(raw);
}

/**
 * Validates an optional numeric text input as a whole number.
 *
 * This is designed for `type="text"` inputs where we want to preserve the raw
 * string (so we can display validation errors instead of letting the browser
 * coerce invalid input to an empty string).
 *
 * Rules:
 * - empty string => valid (returns `undefined` so the field is treated as optional)
 * - non-empty => must be whole-number digits only (no decimals, signs, or whitespace)
 * - value must be >= 0 (customizable error message)
 *
 * Returns a validation error message, or `undefined` when valid.
 */
function validateOptionalWholeNumberInput(
  rawValue: string,
  negativeErrorMessage: string
): string | undefined {
  if (rawValue === "") return undefined;
  if (!/^\d+$/.test(rawValue)) return "Input must be a whole number";

  return new NumberValidator(Number(rawValue))
    .min(0, negativeErrorMessage)
    .noNaN("Input must be a number")
    .validate();
}

enum DETAILEDITORTYPES {
  Schedule,
  Question,
}

interface CreateEMAProps
  extends RouteComponentProps<{
    location: { state: { randomized: boolean; surveyIndex: number } };
  }> {}

const CreateEMA: PageComponent<CreateEMAProps> = (props) => {
  // TODO make this a modal form rather than a page... reduces some of the complexity
  const [displaySchedule, setDisplaySchedule] = useState(false);
  const [displayQuestions, setDisplayQuestions] = useState(false);
  const [validation, setValidation] = useState<
    Record<string, string | undefined>
  >({});
  const [canSwitchDetailEditor, setCanSwitchDetailEditor] = useState(true);

  const [currentQuestion, setCurrentQuestion] = useState<number | undefined>(
    undefined
  );
  const [currentDay, setCurrentDay] = useState<Weekday | undefined>(undefined);
  const [currentDetailEditorSubject, setcurrentDetailEditorSubject] = useState<
    DETAILEDITORTYPES | undefined
  >(undefined);
  const [randomized] = useState(props.location?.state.randomized || false);

  const [studySurvey, setStudySurvey] = useState<StudySurvey>(LoadSurvey());
  // Raw strings for numeric fields — type="number" inputs silently coerce non-numeric
  // text to "", making it impossible to validate, so these fields use type="text" instead.
  const [rawTotalNumberOfDays, setRawTotalNumberOfDays] = useState<string>(() => {
    const survey = LoadSurvey();
    const val =
      survey.exact?.totalNumberOfDays ??
      survey.random?.totalNumberOfDays;
    return val != null ? String(val) : "";
  });
  const [rawDaysBeforeFirstSurveyDelivery, setRawDaysBeforeFirstSurveyDelivery] = useState<string>(() => {
    const survey = LoadSurvey();
    const val =
      survey.exact?.daysBeforeFirstSurveyDelivery ??
      survey.random?.daysBeforeFirstSurveyDelivery;
    return val != null ? String(val) : "";
  });
  const [surveyPrefs, setSurveyPrefs] = useState(
    () =>
      JSON.parse(sessionStorage.getItem("surveyPrefs") as string) ?? {
        ...SurveyPrefsDefaultValue,
      }
  );

  const [
    studyHasSelfInitiatedSurvey,
    setStudyHasSelfInitiatedSurvey,
  ] = useState(false);

  const dashboardSettings = useDashboardSettings(surveyPrefs.studyName);
  const questions = getQuestions();

  // MARK -: utility functions
  useEffect(() => {
    setStudyHasSelfInitiatedSurvey(false); // Reset state before recalculating
    sessionStorage.setItem("surveyPrefs", JSON.stringify(surveyPrefs));

    const studySurveyList = surveyPrefs.studySurveyList;
    // Check if any survey(except the current survey) in the study list has self-initiated survey enabled
    for (let i = 0; i < studySurveyList.length; i++) {
      if (i !== props.location?.state.surveyIndex) {
        const survey = studySurveyList[i];
        if (survey.random || survey.exact) {
          if (survey.random?.selfInitiatedSurvey) {
            setStudyHasSelfInitiatedSurvey(true);
            break;
          } else if (survey.exact?.selfInitiatedSurvey) {
            setStudyHasSelfInitiatedSurvey(true);
            break;
          }
        }
      }
    }

  }, [surveyPrefs]);

  useEffect(() => {
    if (currentDay && !randomized && studySurvey.exact!.weekDeliverySchedule) {
      if (shouldEnsureScheduleEntry(
        currentDay as string,
        (studySurvey.exact!.weekdaysAvailable ?? []) as string[],
        studySurvey.exact!.weekDeliverySchedule
      )) {
        addExactSchedule();
      }
    } else if (
      currentDay &&
      randomized &&
      studySurvey.random!.weekDeliverySchedule
    ) {
      if (shouldEnsureScheduleEntry(
        currentDay as string,
        (studySurvey.random!.weekdaysAvailable ?? []) as string[],
        studySurvey.random!.weekDeliverySchedule
      )) {
        addRandomizedSchedule();
      }
    }
  }, [currentDay]);

  useEffect(() => {
    if (props.location) {
      const surveyListCopy = [...surveyPrefs.studySurveyList];
      surveyListCopy[props.location.state.surveyIndex] = studySurvey;
      setSurveyPrefs({
        ...surveyPrefs,
        studySurveyList: [...surveyListCopy],
      });
    }
  }, [studySurvey]);

  function LoadSurvey(): StudySurvey {
    const index = props?.location?.state.surveyIndex ?? 0;
    return (
      JSON.parse(sessionStorage.getItem("surveyPrefs") as string)
        .studySurveyList[index] ||
      (randomized
        ? { ...RandomizedDefaultSurveyValue, exact: null }
        : { ...ExactDefaultSurveyValue, random: null })
    );
  }

  function validateForm(): boolean {
    const validators = [];

    const errorMsg = "Cannot be Blank";
    //surveyId, notificationHeader, introStepHeader, completionStepHeader
    const surveyIdError = new StringValidator(
      studySurvey.exact?.surveyId ?? studySurvey.random?.surveyId
    )
      .min(1, errorMsg)
      .validate();
    const notificationHeaderError = new StringValidator(
      studySurvey.exact?.notificationHeader ??
        studySurvey.random?.notificationHeader ??
        ""
    )
      .min(1, errorMsg)
      .validate();
    const introStepHeaderError = new StringValidator(
      studySurvey.exact?.introStepHeader ??
        studySurvey.random?.introStepHeader ??
        ""
    )
          .min(1, errorMsg)
          .validate();

    const totalNumberOfDaysError = validateOptionalWholeNumberInput(
      rawTotalNumberOfDays,
      "Total days cannot be negative"
    );

    const daysBeforeFirstSurveyDeliveryError = validateOptionalWholeNumberInput(
      rawDaysBeforeFirstSurveyDelivery,
      "Days cannot be negative"
    );

    const branchingError = new BranchingValidator(questions)
      .ordered()
      .validate();

    validators.push(
      surveyIdError,
      notificationHeaderError,
      introStepHeaderError,
      totalNumberOfDaysError,
      daysBeforeFirstSurveyDeliveryError,
      branchingError
    );

    let completionStepHeaderError = undefined;
    if (
      studySurvey.exact?.completionStepEnabled ??
      studySurvey.random?.completionStepEnabled
    ) {
      completionStepHeaderError = new StringValidator(
        studySurvey.exact?.completionStepHeader ??
          studySurvey.random?.completionStepHeader ??
          ""
      )
        .min(1, errorMsg)
        .validate();
      validators.push(completionStepHeaderError);
    }

    let scheduleControlErrors = undefined;
    if (
      studySurvey.exact?.multipleSurveyBursts ??
      studySurvey.random?.multipleSurveyBursts
    ) {
      const scheduleControlValue = {
        multipleBursts:
          studySurvey.exact?.multipleSurveyBursts ??
          studySurvey.random!.multipleSurveyBursts,
        daysBefore:
          studySurvey.exact?.daysBeforeFirstSurveyDelivery ??
          studySurvey.random?.daysBeforeFirstSurveyDelivery ??
          undefined,
        daysWithin:
          studySurvey.exact?.daysWithinSurveyBurst ??
          studySurvey.random?.daysWithinSurveyBurst ??
          undefined,
        daysBetween:
          studySurvey.exact?.daysBetweenSurveyBursts ??
          studySurvey.random?.daysBetweenSurveyBursts ??
          undefined,
        randomized: randomized,
      };
      scheduleControlErrors = scheduleControlPanelValidation(
        scheduleControlValue
      );
      const { daysBetweenError, daysWithinError } = scheduleControlErrors;
      validators.push(daysBetweenError, daysWithinError);
    }

    // merge all valdiation Records into state
    setValidation({
      ...validation,
      surveyId: surveyIdError,
      notificationHeader: notificationHeaderError,
      introStepHeader: introStepHeaderError,
      completionStepHeader: completionStepHeaderError,
      totalNumberOfDays: totalNumberOfDaysError,
      daysBeforeFirstSurveyDelivery: daysBeforeFirstSurveyDeliveryError,
      questions: branchingError,
      ...scheduleControlErrors,
    });
    return validators.every((validator) => validator === undefined);
  }

  function handleSubmit() {
    const blankBranchDestinations = questionsWithBlankBranchDestinations(
      questions
    );
    if (blankBranchDestinations.length > 0) {
      alertMounter({
        titleText: "Branching Logic Must Be Set",
        message: `Change or remove the branching logic in the following questions before saving: ${blankBranchDestinations.join(
          ", "
        )}`,
        showCancelButton: false,
      });
      return;
    }
    if (validateForm()) {
      // Normalize optional numeric fields: blank input should be persisted as `null`
      // so the attribute is explicitly present as null in DynamoDB.
      const normalizedStudySurvey: StudySurvey = randomized
        ? {
            ...studySurvey,
            random: {
              ...studySurvey.random,
              totalNumberOfDays: parseNullableNumberInput(rawTotalNumberOfDays),
              daysBeforeFirstSurveyDelivery: parseNullableNumberInput(
                rawDaysBeforeFirstSurveyDelivery
              ),
            } as RandomizedDeliverySurvey,
          }
        : {
            ...studySurvey,
            exact: {
              ...studySurvey.exact,
              totalNumberOfDays: parseNullableNumberInput(rawTotalNumberOfDays),
              daysBeforeFirstSurveyDelivery: parseNullableNumberInput(
                rawDaysBeforeFirstSurveyDelivery
              ),
            } as ExactDeliverySurvey,
          };

      // Persist immediately so navigation doesn't race React effects.
      const index = props?.location?.state.surveyIndex ?? 0;
      const updatedSurveyList = [...(surveyPrefs.studySurveyList ?? [])];
      updatedSurveyList[index] = normalizedStudySurvey;
      const updatedPrefs = {
        ...surveyPrefs,
        studySurveyList: updatedSurveyList,
      };
      setStudySurvey(normalizedStudySurvey);
      setSurveyPrefs(updatedPrefs);
      sessionStorage.setItem("surveyPrefs", JSON.stringify(updatedPrefs));

      navigateToCreateStudy();
    } else {
      alertMounter({
        titleText: "Validation Error",
        message:
          "Please fill out all required fields and fix all highlighted fields before leaving the current detail editor.",
        showCancelButton: false,
      });
    }
  }

  function tryToChangeDetailEditorTo(newType: DETAILEDITORTYPES) {
    if (canSwitchDetailEditor) {
      setcurrentDetailEditorSubject(newType);
    } else {
      alertMounter({
        titleText: "Validation Error",
        message:
          "Please fill out all required fields and fix all highlighted fields before leaving the current detail editor.",
        showCancelButton: false,
      });
    }
  }

  // MARK -: Questions
  function getQuestions(): Array<SurveyQuestion | null> {
    return (
      studySurvey.random?.emaQuestions ?? studySurvey.exact?.emaQuestions ?? []
    );
  }

  function toggleContainsRiskAlerts(riskAlertToggle?: boolean | undefined) {
    const hasCurrentRiskAlert = hasRiskRules([...questions]);
    if (!riskAlertToggle && hasCurrentRiskAlert.length > 0) {
      alertMounter({
        titleText: `Cannot Toggle Risk Alerts`,
        message: `This cannot be turned off because risk alerts are currently setup for at least one question in this study. Question IDs: ${hasCurrentRiskAlert.join(
          ", "
        )}`,
        showCancelButton: false,
      });
      return;
    }
    if (randomized) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          containsRiskAlerts: riskAlertToggle,
        } as RandomizedDeliverySurvey,
      });
    } else {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          containsRiskAlerts: riskAlertToggle,
        } as ExactDeliverySurvey,
      });
    }
  }

  function updateQuestions(
    newQuestionsValue?: Array<SurveyQuestion | null>,
    newShuffle?: ShuffleSequence
  ) {
    const surveyQuestions = newQuestionsValue
      ? newQuestionsValue
      : studySurvey.random?.emaQuestions ?? studySurvey.exact?.emaQuestions;
    const containsShuffle = newShuffle !== undefined;
    if (randomized) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          emaQuestions: surveyQuestions,
          shuffleSequence: newShuffle,
          containsShuffledSequence: containsShuffle,
        } as RandomizedDeliverySurvey,
      });
    } else {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          emaQuestions: surveyQuestions,
          shuffleSequence: newShuffle,
          containsShuffledSequence: containsShuffle,
        } as ExactDeliverySurvey,
      });
    }
  }

  //TODO move into QuestionEditor
  const updateQuestion = (key: number) => (
    updatedQuestionValue:
      | TextEntryQuestion
      | BooleanQuestion
      | ScaleQuestion
      | ChoiceQuestion
      | TimeOfDayQuestion
      | TextInfo
  ) => {
    const oldQuestion = getQuestions()[key];
    const oldQuestionValue = getQuestionContent(oldQuestion);

    const questionsCopy = [
      ...updateBranchingForIDChanges(oldQuestionValue, updatedQuestionValue, [
        ...questions,
      ]),
    ];
    const question = questionsCopy[key];

    if (question) {
      if (question.bool) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          bool: updatedQuestionValue as BooleanQuestion,
        });
      } else if (question.choice) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          choice: updatedQuestionValue as ChoiceQuestion,
        });
      } else if (question.scale) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          scale: updatedQuestionValue as ScaleQuestion,
        });
      } else if (question.textEntry) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          textEntry: updatedQuestionValue as TextEntryQuestion,
        });
      } else if (question.timeOfDay) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          timeOfDay: updatedQuestionValue as TimeOfDayQuestion,
        });
      } else if (question.info) {
        questionsCopy[key] = new SurveyQuestion({
          ...question,
          info: updatedQuestionValue as TextInfo,
        });
      }
      updateQuestions(questionsCopy);
    }
  };

  function handleQuestionControlPanel(panelValue: QuestionControlPanelValue) {
    if (canSwitchDetailEditor) {
      if (
        panelValue.currentQuestion !== undefined &&
        panelValue.currentQuestion !== currentQuestion
      ) {
        setCurrentQuestion(panelValue.currentQuestion);
        tryToChangeDetailEditorTo(DETAILEDITORTYPES.Question);
      }
      updateQuestions(panelValue.questions, panelValue.shuffleSequence);
    } else {
      tryToChangeDetailEditorTo(DETAILEDITORTYPES.Question);
    }
  }

  function getQuestionIDs(): string[] {
    return questions.map((item) => {
      let id = "";
      if (item) {
        if (item.bool) {
          id = item.bool.questionId ?? "";
        } else if (item.textEntry) {
          id = item.textEntry.questionId ?? "";
        } else if (item.choice) {
          id = item.choice.questionId ?? "";
        } else if (item.scale) {
          id = item.scale.questionId ?? "";
        } else if (item.timeOfDay) {
          id = item.timeOfDay.questionId ?? "";
        } else if (item.info) {
          id = item.info.questionId ?? "";
        }
      }
      return id;
    });
  }

  //MARK -: Schedule
  function changeDay(key: string, values: string[]) {
    if (canSwitchDetailEditor) {
      const day = (key as unknown) as Weekday;
      setCurrentDay(day);
      if (randomized) {
        let schedules = studySurvey.random!.weekDeliverySchedule;
        if (values.length < 7) {
          schedules = schedules.map((item) => {
            return item ? { ...item, repeatsEveryDay: false } : null;
          });
        }
        // filter out schedules for days not available
        schedules = schedules.filter(
          (item) => item && values.includes(item?.dayOfWeek)
        );
        // add an entry for the day if it was just selected and doesn't have one yet
        // (handles select → remove → re-select the same day without currentDay changing)
        if (shouldEnsureScheduleEntry(key, values, schedules)) {
          schedules = [
            ...schedules,
            {
              dayOfWeek: key as Weekday,
              windowStartHour: RandomizedDeliveryForWeekdayDefaultValue.windowStartHour,
              windowEndHour: RandomizedDeliveryForWeekdayDefaultValue.windowEndHour,
              repeatsEveryDay: false,
            },
          ];
        }
        setStudySurvey({
          ...studySurvey,
          random: {
            ...studySurvey.random,
            weekdaysAvailable: values,
            weekDeliverySchedule: schedules,
          } as RandomizedDeliverySurvey,
        });
      } else {
        // if not all days are selected turn off repeat
        let schedules = studySurvey.exact!.weekDeliverySchedule;
        if (values.length < 7) {
          // filter out schedules for days not available. Need to leave the object intact for display purposes.
          schedules = schedules.map((item) => {
            if (item != null) {
              if (!values.includes(item.dayOfWeek)) {
                return { ...item, exactDeliveriesForDay: [] };
              }
            }
            return item;
          });
          schedules = schedules.map((item) => {
            if (item) {
              const newDeliveries = item.exactDeliveriesForDay.map(
                (delivery) => {
                  return { ...delivery, repeatsEveryDay: false };
                }
              );
              return {
                dayOfWeek: item.dayOfWeek,
                exactDeliveriesForDay: newDeliveries,
              };
            }
            return null;
          });
        }
        // add an entry for the day if it was just selected and doesn't have one yet
        // (handles select → remove → re-select the same day without currentDay changing)
        if (shouldEnsureScheduleEntry(key, values, schedules)) {
          schedules = [
            ...schedules,
            {
              dayOfWeek: key as Weekday,
              exactDeliveriesForDay: [],
            },
          ];
        }
        setStudySurvey({
          ...studySurvey,
          exact: {
            ...studySurvey.exact,
            weekdaysAvailable: values,
            weekDeliverySchedule: schedules,
          } as ExactDeliverySurvey,
        });
      }
    }
    tryToChangeDetailEditorTo(DETAILEDITORTYPES.Schedule);
  }

  function handleScheduleControlPanel(panelValue: ScheduleControlPanelValue) {
    if (randomized) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          multipleSurveyBursts: panelValue.multipleBursts,
          daysBetweenSurveyBursts: panelValue.daysBetween,
          daysWithinSurveyBurst: panelValue.daysWithin,
        } as RandomizedDeliverySurvey,
      });
    } else {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          multipleSurveyBursts: panelValue.multipleBursts,
          daysBetweenSurveyBursts: panelValue.daysBetween,
          daysWithinSurveyBurst: panelValue.daysWithin,
        } as ExactDeliverySurvey,
      });
    }
  }

  function addExactSchedule() {
    if (currentDay) {
      const schedules = [...studySurvey!.exact!.weekDeliverySchedule!];
      schedules.push({
        dayOfWeek: currentDay,
        exactDeliveriesForDay: [],
      });
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          weekDeliverySchedule: schedules,
        } as ExactDeliverySurvey,
      });
    }
  }

  function addRandomizedSchedule() {
    if (currentDay) {
      const schedules = [...studySurvey!.random!.weekDeliverySchedule!];
      schedules.push({
        dayOfWeek: currentDay,
        windowStartHour:
          RandomizedDeliveryForWeekdayDefaultValue.windowStartHour,
        windowEndHour: RandomizedDeliveryForWeekdayDefaultValue.windowEndHour,
        repeatsEveryDay: false,
      });
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          weekDeliverySchedule: schedules,
        } as RandomizedDeliverySurvey,
      });
    }
  }

  const removeSchedule = (key: number) => () => {
    if (randomized) {
      const schedules = [...studySurvey!.random!.weekDeliverySchedule!];
      schedules.splice(key, 1);
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          weekDeliverySchedule: schedules,
        } as RandomizedDeliverySurvey,
      });
    } else {
      const schedules = [...studySurvey!.exact!.weekDeliverySchedule!];
      schedules.splice(key, 1);
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          weekDeliverySchedule: schedules,
        } as ExactDeliverySurvey,
      });
    }
  };

  const updateSchedule = (key: number) => (
    schedule: RandomizedDeliveryWindowForWeekday | ExactDeliveriesForWeekday
  ) => {
    if (randomized) {
      const schedules = [...studySurvey!.random!.weekDeliverySchedule!];
      schedules[key] = schedule as RandomizedDeliveryWindowForWeekday;
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          weekDeliverySchedule: schedules,
        } as RandomizedDeliverySurvey,
      });
    } else {
      const schedules = [...studySurvey!.exact!.weekDeliverySchedule!];
      schedules[key] = schedule as ExactDeliveriesForWeekday;
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          weekDeliverySchedule: schedules,
        } as ExactDeliverySurvey,
      });
    }
  };

  // replaces the entire schedule property
  function replaceSchedule(
    schedule: RandomizedDeliveryWindowForWeekday[] | ExactDeliveriesForWeekday[]
  ) {
    const allWeekdays = Object.keys(Weekday).map((item) => item as Weekday);
    if (randomized) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          weekDeliverySchedule: schedule,
          weekdaysAvailable: allWeekdays,
        } as RandomizedDeliverySurvey,
      });
    } else {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          weekDeliverySchedule: schedule,
          weekdaysAvailable: allWeekdays,
        } as ExactDeliverySurvey,
      });
    }
  }

  // MARK -: Text Fields
  function updateSurveyID(event: FormEvent<HTMLInputElement>) {
    const surveyIDs = surveyPrefs.studySurveyList.map(
      (survey: StudySurvey) =>
        survey.exact?.surveyId ?? survey.random?.surveyId ?? ""
    );
    const error = new StringValidator(event.currentTarget.value)
      .min(3, "EMA ID must contain than three characters")
      .alphaNum("EMA ID can only contain numbers and letters")
      .unique(
        surveyIDs,
        "EMA ID cannot be the same as another EMA ID in the study",
        undefined,
        true
      )
      .unique(
        [SCREENTIME_EMA_NAME],
        "EMA ID is reserved and cannot be used",
        undefined,
        true
      )
      .validate();
    setValidation({
      ...validation,
      surveyId: error,
    });
    if (randomized) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          surveyId: event.currentTarget.value,
        } as RandomizedDeliverySurvey,
      });
    } else {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          surveyId: event.currentTarget.value,
        } as ExactDeliverySurvey,
      });
    }
  }

  const updateTotalNumberOfDays = (
    event: FormEvent<HTMLInputElement>
  ) => {
    const raw = event.currentTarget.value;
    setRawTotalNumberOfDays(raw);

    if (raw !== "" && !/^\d+$/.test(raw)) {
      setValidation({
        ...validation,
        totalNumberOfDays: "Input must be a whole number",
      });
      return;
    }

    const parsedValue = parseNullableNumberInput(raw);
    const error =
      parsedValue === null
        ? undefined
        : new NumberValidator(parsedValue)
            .min(0, "Total days cannot be negative")
            .noNaN("Input must be a number")
            .validate();

    setValidation({ ...validation, totalNumberOfDays: error });
    if (randomized && studySurvey.random) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          totalNumberOfDays: parsedValue,
        },
      });
    } else if (studySurvey.exact) {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          totalNumberOfDays: parsedValue,
        } as ExactDeliverySurvey,
      });
    }
  };

  const updateDaysBeforeFirstSurveyDelivery = (
    event: FormEvent<HTMLInputElement>
  ) => {
    const raw = event.currentTarget.value;
    setRawDaysBeforeFirstSurveyDelivery(raw);

    if (raw !== "" && !/^\d+$/.test(raw)) {
      setValidation({
        ...validation,
        daysBeforeFirstSurveyDelivery: "Input must be a whole number",
      });
      return;
    }

    const parsedValue = parseNullableNumberInput(raw);
    const error =
      parsedValue === null
        ? undefined
        : new NumberValidator(parsedValue)
            .min(0, "Days cannot be negative")
            .noNaN("Input must be a number")
            .validate();

    setValidation({ ...validation, daysBeforeFirstSurveyDelivery: error });
    if (randomized && studySurvey.random) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          daysBeforeFirstSurveyDelivery: parsedValue,
        },
      });
    } else if (studySurvey.exact) {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          daysBeforeFirstSurveyDelivery: parsedValue,
        } as ExactDeliverySurvey,
      });
    }
  };

  const updateStringProp = (prop: keyof RandomizedDeliverySurvey) => (
    event: FormEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>
  ) => {
    const minLengthPerProperty: Record<string, number> = {
      notificationBody: 0,
      notificationHeader: 3,
      introStepBody: 0,
      introStepHeader: 3,
      completionStepBody: 0,
      completionStepHeader: 3,
    };
    const minLength = minLengthPerProperty[prop];
    // check error
    const error = new StringValidator(event.currentTarget.value)
      .min(minLength, `Item must contain at least ${minLength} characters`)
      .validate();
    setValidation({
      ...validation,
      [prop]: error,
    });
    // set value
    if (randomized && studySurvey.random) {
      const randomSurvey = { ...studySurvey.random };
      (randomSurvey[prop] as string) = event.currentTarget.value;
      setStudySurvey({
        ...studySurvey,
        random: randomSurvey,
      });
    } else if (studySurvey.exact) {
      const exactSurvey = { ...studySurvey.exact };
      (exactSurvey[prop] as string) = event.currentTarget.value;
      setStudySurvey({
        ...studySurvey,
        exact: exactSurvey as ExactDeliverySurvey,
      });
    }
  };

  function updateShowCompletion(shouldShow: boolean) {
    if (randomized && studySurvey.random) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          completionStepEnabled: shouldShow,
        },
      });
    } else if (studySurvey.exact) {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          completionStepEnabled: shouldShow,
        },
      });
    }
  }

  function updateSelfInitiatedSurvey(value: boolean) {
    // update the self-initiated survey property in the study survey
    if (randomized && studySurvey.random) {
      setStudySurvey({
        ...studySurvey,
        random: {
          ...studySurvey.random,
          selfInitiatedSurvey: value,
        },
      });
    } else if (studySurvey.exact) {
      setStudySurvey({
        ...studySurvey,
        exact: {
          ...studySurvey.exact,
          selfInitiatedSurvey: value,
        },
      });
    }
  }

  return (
    <VerticalSplitPage
      {...props}
      leftChild={
        <LeftPane>
          {randomized && (
            <Row>
              <h1>Randomized Survey</h1>
              <Spacer />
              <BlockButton onClick={handleSubmit}>Save</BlockButton>
            </Row>
          )}
          {!randomized && (
            <Row>
              <h1>Exact Survey</h1>
              <Spacer />
              <BlockButton onClick={handleSubmit}>Save</BlockButton>
            </Row>
          )}
          <p>
            <Required>
              <strong>EMA ID</strong>
            </Required>
            : what would you like to refer to this survey as internally? This
            identifier will appear in your collected data output, and will not
            be visible to participants. (Ex: DailyOne, Daily2A)
          </p>
          <ValidatedInput
            message={validation.surveyId}
            placeholder={"EMA ID"}
            value={studySurvey.exact?.surveyId ?? studySurvey.random?.surveyId}
            onChange={updateSurveyID}
          />
          <p>
            <Required>
              <strong>Notification header</strong>
            </Required>
            : header text that will be displayed in the notification prompting
            participants to initiate this survey
          </p>
          <ValidatedInput
            message={validation.notificationHeader}
            placeholder={"Notification header"}
            value={
              studySurvey.exact?.notificationHeader ??
              studySurvey.random?.notificationHeader ??
              "Error Saving Header"
            }
            onChange={updateStringProp("notificationHeader")}
          />
          <p>
            <strong>Notification body</strong>: body text that will be displayed
            in the notification prompting participants to initiate this survey
          </p>
          <ValidatedTextArea
            message={validation.notificationBody}
            placeholder={"Notification body"}
            value={
              studySurvey.exact?.notificationBody ??
              studySurvey.random?.notificationBody ??
              "Error Saving Body"
            }
            onChange={updateStringProp("notificationBody")}
          />
          <p>
            <Required>
              <strong>Survey instruction page header</strong>
            </Required>
            :
          </p>
          <ValidatedInput
            message={validation.introStepHeader}
            placeholder={"Instruction header"}
            value={
              studySurvey.exact?.introStepHeader ??
              studySurvey.random?.introStepHeader ??
              "Error Saving Header"
            }
            onChange={updateStringProp("introStepHeader")}
          />
          <p>
            <strong>Survey instruction page body</strong>: brief description of
            the survey to guide participants
          </p>
          <ValidatedTextArea
            message={validation.introStepBody}
            placeholder={"Instruction Body"}
            value={
              studySurvey.exact?.introStepBody ??
              studySurvey.random?.introStepBody ??
              "Error Saving Body"
            }
            onChange={updateStringProp("introStepBody")}
          />
          <TopMarginRow>
            <MarginSpan>
              <strong>Show Completion Page?</strong>
            </MarginSpan>
            <Switch
              checked={
                studySurvey.exact?.completionStepEnabled ??
                studySurvey.random?.completionStepEnabled ??
                true
              }
              onChange={updateShowCompletion}
            />
          </TopMarginRow>
          {(studySurvey.exact?.completionStepEnabled ??
            studySurvey.random?.completionStepEnabled ??
            true) && (
            <div>
              <p>
                <Required>
                  <strong>Survey Completion Header</strong>
                </Required>
                :
              </p>
              <ValidatedInput
                message={validation.completionStepHeader}
                placeholder={"Completion header"}
                value={
                  studySurvey.exact?.completionStepHeader ??
                  studySurvey.random?.completionStepHeader ??
                  "Error Saving Header"
                }
                onChange={updateStringProp("completionStepHeader")}
              />
              <p>
                <strong>Survey Completion Body</strong>: what text would you
                like to display when this survey questions have been completed,
                before the survey is submitted?
              </p>
              <ValidatedTextArea
                message={validation.completionStepBody}
                placeholder={"Completion Body"}
                value={
                  studySurvey.exact?.completionStepBody ??
                  studySurvey.random?.completionStepBody ??
                  "Error Saving Completion Body"
                }
                onChange={updateStringProp("completionStepBody")}
              />
            </div>
          )}
          <p>
            <strong>Total Number of Survey Days</strong>:
          </p>
          <ValidatedInput
            placeholder="Leave empty for indefinite days."
            message={validation.totalNumberOfDays}
            type="text"
            value={rawTotalNumberOfDays}
            onChange={updateTotalNumberOfDays}
          />
          <p>
            <strong>Days Before First Survey Delivery:</strong>
          </p>
          <Spacer />
          <ValidatedInput
            message={validation.daysBeforeFirstSurveyDelivery}
            placeholder="None"
            type="text"
            value={rawDaysBeforeFirstSurveyDelivery}
            onChange={updateDaysBeforeFirstSurveyDelivery}
          />
          {surveyPrefs.selfInitiatedSurveyEnabled &&
            !studyHasSelfInitiatedSurvey && (
              <TopMarginRow>
                <MarginSpan>
                  <strong>Self Initiated EMA?</strong>
                </MarginSpan>
                <Switch
                  checked={
                    studySurvey.exact?.selfInitiatedSurvey ??
                    studySurvey.random?.selfInitiatedSurvey
                  }
                  onChange={updateSelfInitiatedSurvey}
                />
              </TopMarginRow>
            )}
          <hr />
          <Row>
            <SmallButton
              className={"clear"}
              onClick={() => setDisplaySchedule(!displaySchedule)}
            >
              <h3>{displaySchedule ? "v " : "> "} Scheduling</h3>
            </SmallButton>
          </Row>
          <hr />
          {displaySchedule && (
            <ScheduleControlPanel
              daysOnChange={changeDay}
              value={{
                multipleBursts:
                  studySurvey.random?.multipleSurveyBursts ??
                  studySurvey.exact?.multipleSurveyBursts ??
                  false,
                daysWithin:
                  studySurvey.random?.daysWithinSurveyBurst ??
                  studySurvey.exact?.daysWithinSurveyBurst ??
                  undefined,
                daysBetween:
                  studySurvey.random?.daysBetweenSurveyBursts ??
                  studySurvey.exact?.daysBetweenSurveyBursts ??
                  undefined,
              }}
              burstOnChange={handleScheduleControlPanel}
              currentDays={
                randomized
                  ? (studySurvey.random?.weekdaysAvailable as Weekday[])
                  : (studySurvey.exact?.weekdaysAvailable as Weekday[])
              }
              validation={validation}
            />
          )}
          <hr />
          <Row>
            <SmallButton
              className={"clear"}
              onClick={() => setDisplayQuestions(!displayQuestions)}
            >
              <h3>
                {displayQuestions ? "v " : "> "} Questions{" "}
                <ValidationError>{validation.questions}</ValidationError>
              </h3>
            </SmallButton>
          </Row>
          <hr />
          {displayQuestions && (
            <QuestionControlPanel
              value={{
                questions: questions,
                currentQuestion: currentQuestion,
                containsRiskAlerts:
                  studySurvey.random?.containsRiskAlerts ??
                  studySurvey.exact?.containsRiskAlerts,
                shuffleSequence:
                  studySurvey.random?.shuffleSequence ??
                  studySurvey.exact?.shuffleSequence,
              }}
              onChange={handleQuestionControlPanel}
              toggleContainsRiskAlerts={toggleContainsRiskAlerts}
              dashboardSettings={dashboardSettings}
            />
          )}
        </LeftPane>
      }
      rightChild={
        <RightPane>
          {!currentQuestion && !currentDay && <h2>Detail Editor</h2>}
          {currentQuestion !== undefined &&
            currentDetailEditorSubject === DETAILEDITORTYPES.Question && (
              <QuestionEditor
                surveyQuestions={questions}
                currentQuestion={currentQuestion}
                onChange={updateQuestion(currentQuestion)}
                questionIDs={getQuestionIDs()}
                canSwitch={setCanSwitchDetailEditor}
                showRiskToggle={
                  studySurvey.random?.containsRiskAlerts ??
                  studySurvey.exact?.containsRiskAlerts
                }
              />
            )}
          {currentDetailEditorSubject === DETAILEDITORTYPES.Schedule &&
            currentDay && (
              <div>
                <h2>{currentDay}</h2>
                {randomized &&
                  studySurvey &&
                  studySurvey.random &&
                  studySurvey.random.weekDeliverySchedule &&
                  studySurvey.random.weekDeliverySchedule.map((item, key) => {
                    if (item && item.dayOfWeek === currentDay) {
                      return (
                        <RandomizedDeliveryForWeekdayComponent
                          value={item}
                          key={key}
                          onChange={updateSchedule(key)}
                          removeSchedule={removeSchedule(key)}
                          allSchedules={
                            studySurvey.random?.weekDeliverySchedule
                          }
                          onUpdateRepeatsEveryDay={replaceSchedule}
                        />
                      );
                    }
                  })}
                {!randomized &&
                  studySurvey &&
                  studySurvey.exact &&
                  studySurvey.exact.weekDeliverySchedule &&
                  studySurvey.exact.weekDeliverySchedule.map((item, key) => {
                    if (item && item.dayOfWeek === currentDay) {
                      return (
                        <ExactDeliveriesForWeekdayUI
                          value={item}
                          key={key}
                          onChange={updateSchedule(key)}
                          removeSchedule={removeSchedule(key)}
                          canSwitch={setCanSwitchDetailEditor}
                          allSchedules={studySurvey.exact?.weekDeliverySchedule}
                          onUpdateRepeatsEveryDay={replaceSchedule}
                        />
                      );
                    }
                  })}
              </div>
            )}
        </RightPane>
      }
    />
  );
};

export default styled(CreateEMA)`
  background: linear-gradient(315deg, #4b64aa 0%, #3d4f83 100%);
  justify-content: center;
  align-items: center;
`;

const LeftPane = styled("div")``;

const RightPane = styled("div")`
  align-items: center;
`;

const Spacer = styled("div")`
  flex-grow: 1;
`;

const MarginSpan = styled("span")`
  margin: 0 0.4em 0 0;
`;

const TopMarginRow = styled(Row as any)`
  margin: 0.5em 0 0 0;
`;

const ValidationError = styled.span`
  height: 30px;
  color: red;
`;
